import { api } from "@convex/_generated/api"; // Use alias
import { useMutation, useQuery } from "convex/react";
import {
	Check,
	Copy,
	ExternalLink,
	Lock, // Import Lock icon
	QrCode,
	Settings,
	Trash2,
} from "lucide-react";
import QRCode from "qrcode";
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Modal } from "@/components/_shared/Modal";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RaumEinstellungen } from "./RaumEinstellungen"; // Import settings modal

// Einstellungen via Cookies entfernt

// Typ für einen gespeicherten TextShare-Raum (Frontend)
interface RoomItem {
	roomId: string;
	content?: string; // Content might not be needed for list display
	password?: string;
	ownerOnlyEdit: boolean;
	views?: number;
	hatPasswort?: boolean; // Use flag from backend
}

// Typ für den Server-Raum
interface ServerRoomItem {
	raumId: string;
	inhalt?: string; // Make optional as we don't always fetch it
	passwort?: string;
	nurBesitzerEdit: boolean;
	aufrufe?: number;
	hatPasswort?: boolean; // Add flag
}

export default function TextShareForm() {
	const [, navigate] = useLocation();
	const [initialContent, setInitialContent] = useState("");
	const [password, setPassword] = useState("");
	const [ownerOnlyEdit, setOwnerOnlyEdit] = useState(true);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const [lastCreatedRoom, setLastCreatedRoom] = useState<RoomItem | null>(null);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [qrLink, setQrLink] = useState<string>("");
	const [copied, setCopied] = useState<string | null>(null);
	const [qrDataUrl, setQrDataUrl] = useState<string>("");
	const [einstellungenModalAnzeigen, setEinstellungenModalAnzeigen] = useState<
		string | null
	>(null);
	const [editRoomPassword, setEditRoomPassword] = useState("");
	const [editRoomOwnerOnly, setEditRoomOwnerOnly] = useState(true);

	// Convex API Hooks
	const createRoomMutation = useMutation(api.textShare.raumErstellen);
	const raumLoeschenMutation = useMutation(api.textShare.raumLoeschen);
	const raumEinstellungenAktualisierenMutation = useMutation(
		api.textShare.raumEinstellungenAktualisieren,
	);
	// nutzerHash Argument entfernt
	const nutzerRaeumeQuery = useQuery(api.textShare.nutzerRaeumeLaden, {});

	// Map server data to frontend type
	const nutzerRaeume: RoomItem[] = React.useMemo(() => {
		if (!nutzerRaeumeQuery) return [];
		return nutzerRaeumeQuery.map((room: ServerRoomItem) => ({
			roomId: room.raumId,
			ownerOnlyEdit: room.nurBesitzerEdit,
			views: room.aufrufe ?? 0,
			hatPasswort: room.hatPasswort ?? !!room.passwort, // Add flag based on server data
		}));
	}, [nutzerRaeumeQuery]);

	// Toast-Messages nach bestimmter Zeit ausblenden
	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => setSuccessMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => setErrorMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [errorMessage]);

	// TextShare-Raum erstellen
	const createTextRoom = async () => {
		if (!(initialContent.trim() || password.trim())) {
			setErrorMessage(
				"Bitte gib entweder einen Initial-Text oder ein Passwort ein.",
			);
			return;
		}
		// nutzerHash-Prüfung entfernt

		try {
			// nutzerHash Argument entfernt
			const result = await createRoomMutation({
				inhalt: initialContent.trim(), // Trim content
				passwort: password.trim() || undefined,
				nurBesitzerEdit: ownerOnlyEdit,
			});

			setLastCreatedRoom({
				roomId: result.raumId,
				content: initialContent,
				password: password.trim() || undefined,
				ownerOnlyEdit,
				hatPasswort: !!password.trim(),
			});

			setInitialContent("");
			setPassword("");
			setOwnerOnlyEdit(true);

			setSuccessMessage("TextShare-Raum erfolgreich erstellt!");
		} catch {
			setErrorMessage(
				"Fehler beim Erstellen des TextShare-Raums. Bitte versuche es erneut.",
			);
		}
	};

	// Raum löschen
	const handleRaumLoeschen = async (roomId: string) => {
		if (
			!window.confirm(
				"Diesen Raum wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
			)
		) {
			return;
		}
		try {
			await raumLoeschenMutation({ raumId: roomId });
			setSuccessMessage("Raum gelöscht.");
			// Query ist reaktiv; UI aktualisiert sich automatisch
			if (lastCreatedRoom?.roomId === roomId) {
				setLastCreatedRoom(null);
			}
		} catch {
			setErrorMessage("Löschen fehlgeschlagen.");
		}
	};

	// Link kopieren
	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(
			() => {
				setCopied(text);
				setSuccessMessage("Link in die Zwischenablage kopiert!");
				setTimeout(() => setCopied(null), 2000);
			},
			() => {
				setErrorMessage("Kopieren in die Zwischenablage fehlgeschlagen.");
			},
		);
	};

	// Ablauf-Anzeige entfernt

	// Gibt die Basis-URL zurück
	const getBaseUrl = () => window.location.origin;

	// Erstelle den vollständigen Raum-Link
	const getFullRoomUrl = (roomId: string) => `${getBaseUrl()}/text/${roomId}`;

	// QR-Code anzeigen
	const handleShowQR = (roomId: string) => {
		setQrLink(getFullRoomUrl(roomId));
		setQrModalOpen(true);
	};

	// Link-Klick kopiert
	const handleLinkClick = (roomId: string) => {
		const fullUrl = getFullRoomUrl(roomId);
		copyToClipboard(fullUrl);
	};

	// Zum TextShare-Raum navigieren
	const goToRoom = (roomId: string) => {
		navigate(`/text/${roomId}`);
	};

	// Einstellungen bearbeiten Modal öffnen
	const handleEditSettings = (roomId: string) => {
		const roomData = nutzerRaeumeQuery?.find(
			(room: ServerRoomItem) => room.raumId === roomId,
		);
		if (roomData) {
			setEditRoomPassword(roomData.passwort || "");
			setEditRoomOwnerOnly(roomData.nurBesitzerEdit ?? true); // Default to true if undefined
		} else {
			// Fallback or error handling if room data not found (shouldn't happen ideally)
			setEditRoomPassword("");
			setEditRoomOwnerOnly(true);
		}
		setEinstellungenModalAnzeigen(roomId);
	};

	// QR-Code-Modal
	useEffect(() => {
		if (qrModalOpen && qrLink) {
			QRCode.toDataURL(qrLink, { width: 240, margin: 1 }).then(setQrDataUrl);
		} else {
			setQrDataUrl("");
		}
	}, [qrModalOpen, qrLink]);

	// Rendert eine TextShare-Raumzeile
	const renderRoomItem = (room: RoomItem) => {
		const roomId = room.roomId;
		const fullUrl = getFullRoomUrl(roomId);

		// Live-Views für diesen Raum holen
		const liveViews =
			nutzerRaeumeQuery?.find((r: ServerRoomItem) => r.raumId === roomId)
				?.aufrufe ?? 0;

		return (
			<div className="mb-0 flex flex-col items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3 md:flex-row dark:border-gray-700 dark:bg-gray-800">
				<div className="min-w-0 flex-1">
					<button
						className={`flex max-w-full items-center gap-2 overflow-x-auto rounded-lg px-2 py-1 font-medium font-mono text-lg transition-all ${
							fullUrl
								? "group cursor-pointer text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
								: "cursor-not-allowed text-gray-400 dark:text-gray-500"
						}`}
						disabled={!fullUrl}
						onClick={() => handleLinkClick(roomId)}
						type="button"
					>
						{room.hatPasswort && (
							<Lock className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
						)}
						<span className="truncate">
							{getBaseUrl()}/text/
							<span className="text-blue-600 dark:text-blue-400">{roomId}</span>
						</span>
						{copied === fullUrl ? (
							<Check className="h-5 w-5 shrink-0 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
						) : (
							<Copy className="h-5 w-5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
						)}
					</button>

					{/* Details-Pille */}
					<div className="mt-2 flex flex-wrap items-center gap-2 text-xs md:flex-nowrap">
						{/* Bearbeitungsrechte */}
						<div className="inline-flex items-center rounded-full border border-gray-100 bg-white px-3 py-1 dark:border-gray-700 dark:bg-gray-700/50">
							<span className="mr-1 font-medium text-gray-500 dark:text-gray-400">
								Bearbeitung:
							</span>
							<span className="text-blue-600 dark:text-blue-400">
								{room.ownerOnlyEdit ? "Nur für dich" : "Für alle"}
							</span>
						</div>

						{/* Views */}
						<div className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 dark:border-blue-800/30 dark:bg-blue-900/30">
							<svg
								className="mr-1 h-3.5 w-3.5 text-blue-500"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
								/>
								<path
									d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
								/>
							</svg>
							<span className="font-medium text-blue-600 dark:text-blue-400">
								{liveViews}x angezeigt
							</span>
						</div>
					</div>
				</div>

				<div className="flex items-center gap-2 md:flex-col">
					<div className="flex gap-1">
						<Button
							className="h-8 w-8 rounded-full hover:bg-red-50 dark:hover:bg-red-900/30"
							onClick={() => handleRaumLoeschen(roomId)}
							size="icon"
							title="Raum löschen"
							type="button"
							variant="ghost"
						>
							<Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
						</Button>
						<Button
							className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
							onClick={() => handleEditSettings(roomId)}
							size="icon"
							title="Einstellungen bearbeiten"
							type="button"
							variant="ghost"
						>
							<Settings className="h-4 w-4" />
						</Button>
						<Button
							className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
							onClick={() => handleShowQR(roomId)}
							size="icon"
							title="QR-Code anzeigen"
							type="button"
							variant="ghost"
						>
							<QrCode className="h-4 w-4" />
						</Button>
						<Button
							className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
							onClick={() => goToRoom(roomId)}
							size="icon"
							title="Raum öffnen"
							type="button"
							variant="ghost"
						>
							<ExternalLink className="h-4 w-4" />
						</Button>
					</div>
				</div>
			</div>
		);
	};

	// Einstellungen speichern
	const handleSaveSettings = async () => {
		// nutzerHash-Prüfung entfernt
		if (!einstellungenModalAnzeigen) return;

		try {
			// nutzerHash Argument entfernt
			await raumEinstellungenAktualisierenMutation({
				raumId: einstellungenModalAnzeigen,
				passwort: editRoomPassword.trim() || undefined,
				nurBesitzerEdit: editRoomOwnerOnly,
			});

			setSuccessMessage("Einstellungen erfolgreich aktualisiert.");
			setEinstellungenModalAnzeigen(null);
		} catch {
			setErrorMessage("Fehler beim Aktualisieren der Einstellungen.");
		}
	};

	return (
		<div className="flex flex-col gap-8">
			{/* Toasts */}
			<Toast
				message={successMessage || ""}
				onClose={() => setSuccessMessage(null)}
				visible={!!successMessage}
			/>
			<Toast
				message={errorMessage || ""}
				onClose={() => setErrorMessage(null)}
				type="error"
				visible={!!errorMessage}
			/>

			{/* TextShare Formular */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Neuen TextShare erstellen
				</h2>
				<div className="space-y-4">
					<div>
						<Label
							className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
							htmlFor="initial-content"
						>
							Initial-Text
						</Label>
						<Textarea
							className="min-h-[120px] whitespace-pre-wrap border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="initial-content"
							onChange={(e) => setInitialContent(e.target.value)}
							placeholder="Gib hier den initialen Text ein..."
							value={initialContent}
						/>
					</div>

					<div className="mt-2 flex flex-col items-center gap-4 md:flex-row">
						<div className="flex w-full flex-1 items-center md:w-auto">
							<Label className="sr-only" htmlFor="password-input">
								Passwort (optional)
							</Label>
							<Input
								className="h-12 border-gray-200 bg-white transition-all focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
								id="password-input"
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Passwort für den Zugriff (optional)"
								type="password"
								value={password}
							/>
						</div>
						<div className="flex w-full items-center space-x-2 md:w-auto">
							<Switch
								checked={ownerOnlyEdit}
								className="h-5"
								id="owner-only-edit"
								onCheckedChange={setOwnerOnlyEdit}
							/>
							<Label
								className="text-gray-600 dark:text-gray-300"
								htmlFor="owner-only-edit"
							>
								Nur ich darf bearbeiten
							</Label>
						</div>
						<Button
							className="h-12 w-full transition-all hover:bg-blue-100 md:w-auto dark:hover:bg-blue-900/30"
							onClick={createTextRoom}
							type="button"
							variant="secondary"
						>
							Raum erstellen
						</Button>
					</div>
				</div>

				{/* Letzter erstellter Raum */}
				{lastCreatedRoom && (
					<div className="mt-6 border-gray-100 border-t pt-4 dark:border-gray-700">
						<h3 className="mb-4 font-medium text-gray-700 text-lg dark:text-gray-200">
							Dein erstellter TextShare-Raum
						</h3>
						{renderRoomItem(lastCreatedRoom)}
					</div>
				)}
			</section>

			{/* TextShare-Verlauf */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Deine letzten TextShares
				</h2>

				{nutzerRaeume.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-12 text-center">
						<svg
							className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
							/>
						</svg>
						<p className="text-gray-500 text-lg dark:text-gray-400">
							Deine TextShare-Räume erscheinen hier
						</p>
						<p className="mt-2 text-gray-400 text-sm dark:text-gray-500">
							Erstelle einen TextShare, um ihn im Verlauf zu sehen
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{nutzerRaeume.map((room) => (
							<div
								className="overflow-hidden rounded-lg border border-gray-100 transition-shadow hover:shadow-sm dark:border-gray-700"
								key={room.roomId}
							>
								{renderRoomItem(room)}
							</div>
						))}
					</div>
				)}
			</section>

			{/* QR-Code Modal */}
			<Modal
				closeOnOutsideClick
				isOpen={qrModalOpen}
				onClose={() => setQrModalOpen(false)}
				title="QR-Code für TextShare"
			>
				<div className="flex flex-col items-center justify-center gap-6 p-6">
					{qrDataUrl ? (
						<div className="rounded-xl bg-white p-4 shadow-inner">
							<img alt="QR-Code" className="h-64 w-64" src={qrDataUrl} />
						</div>
					) : (
						<div className="flex h-64 w-64 items-center justify-center rounded-xl bg-gray-50 dark:bg-gray-800">
							<div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
						</div>
					)}
					<div className="max-w-sm break-all text-center text-gray-600 text-sm dark:text-gray-300">
						{qrLink}
					</div>
					<Button
						className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
						onClick={() => copyToClipboard(qrLink)}
						type="button"
					>
						<Copy className="h-4 w-4" /> Link kopieren
					</Button>
				</div>
			</Modal>

			{/* Einstellungen Modal */}
			{einstellungenModalAnzeigen && (
				<RaumEinstellungen
					isOpen={!!einstellungenModalAnzeigen}
					nurBesitzerEdit={editRoomOwnerOnly}
					onClose={() => setEinstellungenModalAnzeigen(null)}
					onNurBesitzerEditChange={setEditRoomOwnerOnly}
					onPasswortChange={setEditRoomPassword}
					onSpeichern={handleSaveSettings}
					passwort={editRoomPassword}
				/>
			)}
		</div>
	);
}
